/**
 * 문항 카드 AI 액션 상태 — 빌드 화면의 다른 전역 원자와 같은 jotai 원자.
 * 서버 세션이 없으므로 "한 번에 하나" 는 클라이언트가 지킨다(PRD §6).
 */
import { atom } from 'jotai';

import { type RunnableQuestionCardAiAction } from '../types/questionCardAi.types';

export type RunningQuestionCardAiAction = {
  quizId: string;
  action: RunnableQuestionCardAiAction;
  controller: AbortController;
  startedAt: number;
};

export type QuestionCardAiSlot = {
  quizId: string;
  status: 'running' | 'error';
  code?: string;
  /** 이 슬롯을 연 액션 — 실패 카드의 "다시 시도" 가 같은 액션을 다시 돌리는 데 쓴다. */
  action?: RunnableQuestionCardAiAction;
  /** 요청 시점의 채움 대상 슬롯 id. 요청 중 새로 추가된 슬롯에는 스켈레톤을 그리지 않는다. */
  slotIds?: readonly string[];
};

/** 실행 중 액션 — null 이 아니면 모든 카드의 ⚡ 가 잠긴다. */
export const runningQuestionCardAiActionAtom =
  atom<RunningQuestionCardAiAction | null>(null);

/**
 * 카드(quizId)별 슬롯. 실행은 한 번에 하나지만 실패 슬롯은 카드마다 남는다 — 단일 값이면
 * 카드 B 의 새 작업이 카드 A 의 실패·재시도 UI 를 덮어쓴다.
 */
export type QuestionCardAiSlots = Readonly<Record<string, QuestionCardAiSlot>>;

/** 유사 문제 스켈레톤 자리(원본 카드 id 기준) — running 이거나 error 로 남는다. */
export const similarSlotAtom = atom<QuestionCardAiSlots>({});

/** 선지 채움 중/실패 카드. */
export const fillSlotAtom = atom<QuestionCardAiSlots>({});

export const putSlot = (
  slots: QuestionCardAiSlots,
  slot: QuestionCardAiSlot,
): QuestionCardAiSlots => ({ ...slots, [slot.quizId]: slot });

/** quizId 의 슬롯을 뺀다. onlyIf 가 있으면 그 상태일 때만. 없으면 같은 객체를 돌려준다. */
export const dropSlot = (
  slots: QuestionCardAiSlots,
  quizId: string,
  onlyIf?: QuestionCardAiSlot['status'],
): QuestionCardAiSlots => {
  const own = slots[quizId];
  if (own == null || (onlyIf && own.status !== onlyIf)) return slots;
  const { [quizId]: _dropped, ...rest } = slots;
  return rest;
};
